'use client';

import { getCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import { ParamsProps } from '@/libs/interfaceProps/paramsProps';
import { assetPath } from '@/libs/assetPath/assetPath';
import { fulfillmentPath } from '@/libs/fulfillmentPath/fulfillmentPath';

/**
 * This function that returns a download handler for lead drop actions.
 * Uses Next.js router and cookies to determine navigation logic.
 *
 * @param params - Parameters for asset/fulfillment path (typically includes lang)
 * @param queryParams - Optional query string to append to URLs
 * @returns An async click handler for download actions
 */
export default function LeadDropDownloadBtn(params: ParamsProps, queryParams = '') {
  // Get Next.js router instance for navigation
  const router = useRouter();
  // Get the 'crs' cookie to check user/session state
  const crs = getCookie('crs');

  // Return an async handler function for click events
  return async (e: React.MouseEvent, resId: string, hasVendorQuestions: boolean) => {
    e.preventDefault();

    // If the asset has vendor questions, always go to the asset path (form)
    if (hasVendorQuestions) {
      router.push(assetPath({
        resId: resId,
        locale: params?.lang,
        c: false,
        peId: ''
      }) + queryParams);
      return;
    }

    // If the user does not have a 'crs' cookie, go to the asset path (form)
    if (!crs) {
      router.push(assetPath({
        resId: resId,
        locale: params?.lang,
        c: false,
        peId: ''
      }) + queryParams);
      return;
    }
    try {
      const leadDropResponse = await fetch(`/hub/services/leadDropFulfillment?resId=${resId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (leadDropResponse.status === 200) {
        router.push(fulfillmentPath({
          resId: resId,
          locale: params.lang
        }) + queryParams);
      } else {
        // If fulfillment fails, fallback to the asset path (form)
        router.push(assetPath({
          resId: resId,
          locale: params?.lang,
          c: false,
          peId: ''
        }) + queryParams);
      }
    } catch (error) {
      console.error('Error during lead drop fulfillment:', error);
      // If fulfillment fails, fallback to the asset path (form)
      router.push(assetPath({
        resId: resId,
        locale: params?.lang,
        c: false,
        peId: ''
      }) + queryParams);
    }
  };
}